import {
    Avatar, Backdrop, Button, createTheme, Dialog, DialogContent,
    DialogContentText, DialogTitle, Grid, IconButton, List,
    ListItem, ListItemAvatar, ListItemText, MuiThemeProvider,
    SelectProps,
    styled,
    TextField, Typography
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Add as AddIcon, CloudUpload, Delete as DeleteIcon, PictureAsPdf } from '@material-ui/icons';
import Tooltip from '@mui/material/Tooltip';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import SelectInvoiceType from '../../../components/Form/SelectInvoiceType';
import AlertDialog from '../../../components/ui/AlertDialog';
import ButtonUI from '../../../components/ui/ButtonUI';
import { useAuth } from '../../../hooks/auth';
import { MvmInvoice } from '../../../interfaces/Mvm';
import MvmApi from '../../../services/MvmApi';
import getDefaultGridOptions from '../../../utils/componentUtils';
import utilsDate from '../../../utils/Date';
import { FileUploadUtil } from '../../../utils/FileUpload';
import { useStyles } from '../Style';

type props =  any;

interface IFormInput {
    invoiceKey: any;
    invoiceType: any;
}

const MvmInvoiceList: React.FC<props> = ({ control, title, name, allowAdd, allowDelete, Mvm, onAdd, allowUploadFile=true, setTypeManually=false }) => {

    const classes = useStyles();
    const { t } = useTranslation("Mvm");
    const { t: _t } = useTranslation("General");
    const { enqueueSnackbar } = useSnackbar();
    const { user, isCampoLargo, isMopar, isPortoReal } = useAuth();
    const [circularLoading, setCircularLoading] = useState(false);
    const [selectInvoiceType, setSelectInvoiceType] = useState(false);
    const [unknownTypeInvoice, setUnknownTypeInvoice] = useState<null | MvmInvoice>(null);
    const { fields: _fields, append, remove, update,  } = useFieldArray({ control, name: name });
    const fields = _fields as Record<string, any>[];
    const [invoiceTypes, setInvoiceTypes] = useState<string[]>([ 'V', 'E', 'A', 'G', 'I', 'M', 'P', 'R', 'T']);
    const { t: it } = useTranslation(isMopar() ? "MvmInvoiceMopar" : "MvmInvoice");
    const [loadingFileInvoice, setLoadingFileInvoice] = useState<string>('')

    const refFieldInvoiceKey = useRef<HTMLInputElement>(null)
    const refFieldInvoiceType = useRef<SelectProps | null>(null)
    
    const { control: internalControl, reset, getValues } = useForm<IFormInput>({
        defaultValues: {
            invoiceKey:  '', 
            invoiceType: null            
        }
    })

    useEffect(() => {
        //Campo Largo tem um tipo especifico de NF, que eh a nota Triangular (tipo=C)
        if (isCampoLargo() || isMopar())
            setInvoiceTypes([ 'V', 'E', 'A', 'G', 'I', 'M', 'P', 'R', 'T', 'C' ]);
        else
            setInvoiceTypes([ 'V', 'E', 'A', 'G', 'I', 'M', 'P', 'R', 'T' ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user.company]);

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''
                }
            }
        }
    })

    const columns = [
        {
            name: 'id',
            options: {
                display: false,
            }
        },
        {
            name: 'invoiceKey',
            label: String(t('invoiceKey')),
            options: {
                setCellProps: () => ({
                    align: "left",
                }),
            }
        },
        {
            name: 'invoiceNumber',
            label: String(t('invoiceNumber')),

            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                })    
            }
        },
        {
            name: 'invoiceDate',
            label: String(t('invoiceDate')),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return utilsDate.DateShort(value);
                }
            }
        },
        {
            name: 'invoiceType',
            label: String(t('invoiceType')),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                }),
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return it("invoiceType_" + value);
                }
            }
        },
        {
            name: "",
            options: {
                display: !isPortoReal(),
                setCellProps: () => ({
                    align: "right"
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
                    renderButtons(tableMeta.rowIndex)
                )
            }
        },
    ]

    // Verificar se a nota está em contingência (tpEmis)
    // O caractere que indica se a nota foi emitida em contingência é o 34
    // 1 = Emissão normal (não em contingência)
    // 2 = Contingência FS-IA, com impressão do DANFE em formulário de segurança
    // 3 = Contingência SCAN (Sistema de Contingência do Ambiente Nacional)
    // 4 = Contingência DPEC (Declaração Prévia da Emissão em Contingência)
    // 5 = Contingência FS-DA, com impressão do DANFE em formulário de segurança
    // 6 = Contingência SVC-AN (SEFAZ Virtual de Contingência do AN)
    // 7 = Contingência SVC-RS (SEFAZ Virtual de Contingência do RS)
    const isContingency = (invoiceKey: string) : boolean => {
        return invoiceKey.substring(34, 35) !== '1'
    }
    
    const associateFile = async (event: any, rowIndex: number) => {
        var el = fields[rowIndex]
        if(event.target.files && event.target.files[0]) {

            const fileType = event.target.files[0].type
            const invoiceKey = el['invoiceKey']
            const fileName = `${invoiceKey}.pdf`
            let fileAlias = event.target.files[0].name

            if (fileAlias.length >= 15) {
                const aliasStart = fileAlias.substring(0, 7)
                const aliasEnd = fileAlias.substring(fileAlias.length - 4, fileAlias.length)
                fileAlias =  `${aliasStart}.. ${aliasEnd}`
            }


            if (fileType !== 'application/pdf') {
                enqueueSnackbar(_t("fileValidationPDF"), { variant: 'warning' })
                return
            }

            setLoadingFileInvoice(invoiceKey)


            let fileBytes = null;
            const inputFile = event.target.files[0];

            //Ler bytes do arquivo:
            await FileUploadUtil.getUploadedFileContent({ files: [ inputFile ] }).then((res) => {
                const response = res[0];
                fileBytes = response !== null ? response.content.toString().split(',')[1] : "";
            });
            
            MvmApi.UploadFile({
                folderName: 'Mvm',
                fileFullName: fileName,
                originalFileName: fileName,
                file: fileBytes,
                name: inputFile.name,
                contentType: inputFile.type,
            })
            .then(() => {
                el['fileAlias'] = fileAlias
                el['hasFile'] = true
                enqueueSnackbar(_t('fileUploadSuccess'), { variant: 'success' })
                update(rowIndex, el)
            })
            .finally(() => setLoadingFileInvoice(''))

        }
    }

    const downloadFile = (invoiceKey: string) => {
        
        setLoadingFileInvoice(invoiceKey)
        MvmApi.DownloadFile(`${invoiceKey}.pdf`, 'Mvm')
            .then((resp: any) => {
                const byteCharacters = atob(resp.content);

                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);

                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = invoiceKey;
                link.click();
            })
            .finally(()=> setLoadingFileInvoice(''))
    }

    const renderButtons = (rowIndex: any) => {
        if (fields === undefined || fields.length <= rowIndex) return <></>;

        let justAdded = fields[rowIndex]['mvmId'] === 0;
        
        const contingency = isContingency(fields[rowIndex]['invoiceKey'])

        const VisuallyHiddenInput = styled('input')({
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            height: 1,
            overflow: 'hidden',
            position: 'absolute',
            bottom: 0,
            left: 0,
            whiteSpace: 'nowrap',
            width: 1,
            border: 'none',
            teste: "tese"
          });

        var uploadLabel = <></>
        let hasFile = false 

        if (fields[rowIndex]['fileAlias'])
        {
            hasFile = true
            uploadLabel = <span>{fields[rowIndex]['fileAlias']}</span>
        }
        else if (!justAdded && fields[rowIndex]['hasFile']) {
            hasFile = true
            uploadLabel = <span>{_t('selectFileContingencyInvoice')}</span>
        }
        else
            uploadLabel = <span style={{color: 'red'}}>{_t('selectFileContingencyInvoice')}</span>
        
        return (
        <>
            {contingency && 
            <>
                {loadingFileInvoice === fields[rowIndex]['invoiceKey'] ?
                    <CircularProgress color="inherit" size={10} />
                : 
                    <>
                        {allowUploadFile && <Button style={{marginRight: 10}}
                            component="label" 
                            onChange={(e: any) => associateFile(e, rowIndex)} 
                            endIcon={<CloudUpload />}>
                            {uploadLabel}
                            <VisuallyHiddenInput type="file" />
                        </Button>}

                        {hasFile &&
                            <IconButton size='small' edge="end" onClick={() => downloadFile(fields[rowIndex]['invoiceKey'])}>
                                <PictureAsPdf />
                            </IconButton>}
                    </>
                }
            </>}
            
            {((allowAdd && justAdded) || allowDelete) && 
            <AlertDialog
                dialogTitle={t("dialogDeleteTitle")}
                dialogContentText={t("dialogDeleteContentText")}
                cancelTextButton={t("cancelDeleteTextButton")}
                confirmTextButton={t("confirmDeleteTextButton")}
                confirmDialogFunction={() => removeMvmInvoiceFromList(rowIndex)}
                Button={
                    <IconButton size='small' edge="end" style={{marginLeft: 10}}>
                        <DeleteIcon />
                    </IconButton>
                }
            />}
        </>
        );
    }

   
    async function AddMvmInvoiceToList() {
        if (circularLoading)
            return false;
            
        setCircularLoading(true);
        var invoiceKeySearch = refFieldInvoiceKey.current
        if (!invoiceKeySearch)
        {
            setCircularLoading(false);
            return
        }

        var invoiceKey = String(invoiceKeySearch.value)

        if (invoiceKey.length !== 44) {
            enqueueSnackbar(t("invoiceKeyNot44CharValidation"), { variant: 'warning' })
            setCircularLoading(false);
            return
        }

        var data = getValues()
        if (setTypeManually && (!data.invoiceType || !data.invoiceType.code)) {
            enqueueSnackbar(t("invoiceTypeIsRequired"), { variant: 'warning' })
            setCircularLoading(false);
            return
        }

        var existingInvoice = false
        fields.forEach((field) => {
            if ((field as any).invoiceKey === invoiceKey){
            existingInvoice = true
            }
        })

        if (existingInvoice) {
            enqueueSnackbar(t("mvmInvoiceOnList"), { variant: 'warning' })
            setCircularLoading(false)
            return
        }

        var validInvoice = true
        await MvmApi.validateInvoicesInUse({
            mvmId: (Mvm?.id ?? 0),
            invoiceKeys: [ invoiceKey ]
          })
        .catch((err) => {
            setCircularLoading(false)
            validInvoice = false
        })

        if(!validInvoice) {
            return
        }

        let mvmInvoiceTemp: any;
        
        await MvmApi.getInvoiceData((Mvm?.id ?? 0), (Mvm?.companyId ?? 0), user.company.plantID, invoiceKey).then(res => { 
            mvmInvoiceTemp = res.data;
        })
        .catch(() => {setCircularLoading(false)})

        if (setTypeManually) {
            mvmInvoiceTemp.invoiceType = data.invoiceType.code
            reset()
        }

        //So adicionar na lista caso saiba o tipo da nota. Senao, usuario deve escolher
        if (mvmInvoiceTemp.invoiceType !== null)
        {
            addInvoiceToList(mvmInvoiceTemp)
        }
        else 
        {
            setUnknownTypeInvoice(mvmInvoiceTemp)
            setSelectInvoiceType(true)
        }

        invoiceKeySearch.value = ""

        if (onAdd && mvmInvoiceTemp && mvmInvoiceTemp.invoiceSupplierId)
            onAdd({ 
                code: mvmInvoiceTemp.invoiceSupplierId.toString(), 
                label: mvmInvoiceTemp.invoiceSupplierId.toString() + ' - '  + mvmInvoiceTemp.invoiceSupplierCorporateName, 
                supplierCorporateName: mvmInvoiceTemp.invoiceSupplierCorporateName, 
                uf: mvmInvoiceTemp.supplierUf })

        setCircularLoading(false)
    }

    const removeMvmInvoiceFromList = (index: number) => {
        remove(index)
    }

    //Adicionar nota fiscal na lista da grid:
    function addInvoiceToList(mvmInvoice:any) {
        mvmInvoice.id = 0; //garantir que id = 0 (nova NF inserida na lista): Se a nf veio do elog, pode ter vindo com o id_nota_Fiscal preenchido neste campo erroneamente
        append(mvmInvoice);
        enqueueSnackbar(t("mvmInvoiceAddSuccess"), { variant: 'success' });
    }

    //Usuario escolheu o tipo da nota fiscal:
    async function handleChooseInvoiceType(invoiceType: string) {
        let mvmInvoice = unknownTypeInvoice;

        if (invoiceType === 'cancel')
        {
            setSelectInvoiceType(false);
            setUnknownTypeInvoice(null);
            return;
        }

        //Preencher o tipo e incluir na lista
        if (mvmInvoice !== null)
        {
            mvmInvoice.invoiceType = invoiceType;
            addInvoiceToList(mvmInvoice);
            setSelectInvoiceType(false);
        }
    }

    return (
        <Grid style={{ marginTop: 20, width: "98%", marginLeft: 10, marginBottom: 10 }}>
            <Typography className={classes.root} style={{ marginLeft: 0 }} >
                {title ? title : t("invoices")}
            </Typography>

            {!allowAdd ? <Grid /> :
                <Grid container spacing={1} style={{ marginBottom: 10 }}>
                    <Grid item >
                        <TextField
                            label={t("invoiceKey")}
                            name="invoiceKeySearch"
                            type='text'
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    AddMvmInvoiceToList()
                                    refFieldInvoiceKey.current?.focus()
                                }
                            }}
                            onInput={(e) => { 
                                (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.slice(0, 44);
                            }}
                            style={{ width: 400 }}
                            inputRef={refFieldInvoiceKey} 
                            disabled={Mvm?.entranceDate} />
                    </Grid>
                    {setTypeManually && 
                    <Grid item xs={2}>
                        <SelectInvoiceType 
                            name='invoiceType'
                            label={it('invoiceType')}
                            selectRef={refFieldInvoiceType}
                            control={internalControl}
                            style={{marginTop: 0, marginRight: 10}}
                            disabled={Mvm?.entranceDate}
                        />
                    </Grid>}
                    <Grid>
                        <Tooltip title={!Mvm?.entranceDate ? String(t('invoiceWithEntrance')) : ''}> 
                            <ButtonUI
                                onClick={() => { AddMvmInvoiceToList(); refFieldInvoiceKey.current?.focus()} }
                                color="primary"
                                variant="contained"
                                style={{ maxWidth: '40px', maxHeight: '38px', minWidth: '40px', minHeight: '38px', marginTop: 15, marginLeft: '10px' }}
                                disabled={Mvm?.entranceDate}>
                                <AddIcon />
                            </ButtonUI> 
                        </Tooltip>
                    </Grid>
                </Grid> 
            }

            <Backdrop style={{ zIndex: 9999 }} open={circularLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Typography className={classes.paper} style={{ marginLeft: 0 }} >
                {_t("totalRecords")} {fields ? fields.length : '0'}
            </Typography>
            <MuiThemeProvider theme={myTheme} >
                <MUIDataTable
                    title=""
                    data={fields}
                    columns={columns}
                    options={{
                        ...getDefaultGridOptions(_t),
                        serverSide: false,
                        pagination: false,
                        sort: false,
                        selectToolbarPlacement: "none",
                        filter: false,
                        search: false,
                        download: false,
                        print: false,
                        viewColumns: false,
                        selectableRows: 'none',
                    }}
                />
            </MuiThemeProvider>

            
            {/* Dialog de selecao do tipo da NF */ }
            <Dialog open={selectInvoiceType && !setTypeManually} scroll="body">
                <DialogTitle>{it("unknownInvoiceTypeTitle")}</DialogTitle>
                <DialogContent><DialogContentText>{it("unknownInvoiceTypeText")}</DialogContentText></DialogContent>
                <List>
                    <ListItem button key={'cancel'} onClick={() => handleChooseInvoiceType('cancel')}>
                        <ListItemAvatar>
                            <Avatar>
                            <DeleteIcon />
                            </Avatar>
                        </ListItemAvatar>
                        <ListItemText>{it("unknownInvoiceTypeCancel")}</ListItemText>
                    </ListItem>
                    {invoiceTypes.map((x) => {
                        return (
                            <ListItem button key={x} onClick={() => handleChooseInvoiceType(x)}>
                                <ListItemAvatar>
                                    <Avatar>
                                    <AddIcon />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText>{it("invoiceType_" + x)}</ListItemText>
                            </ListItem>
                        );
                    })}
                </List>
            </Dialog>
        </Grid>
    )
}

export default MvmInvoiceList